import { useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import ServerPaginatedTable from 'src/components/ServerPaginatedTable/ServerPaginatedTable';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import { ExportFormat, FileExportMenu } from 'src/components/FileExportMenu/FileExportMenu';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { useAuth } from 'src/hooks/useAuth';
import { RoleEnum } from 'src/api';
import { useFetchAllQuotations, useQuotations } from 'src/quotations/api';
import type { Quotation, QuotationFilters } from 'src/quotations/types';
import { downloadCSV, downloadXLSX } from 'src/utils';
import { createColumnsData } from './columnsData';
import { buildExportData } from './exportUtils';
import QuotationDetailOverlay from './QuotationDetailOverlay';
import './QuotationsHistoryPage.scss';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const EMPTY_FILTERS: QuotationFilters = { search: '', dealer: '', createdAtFrom: '', createdAtTo: '' };

const sameFilters = (a: QuotationFilters, b: QuotationFilters) =>
	a.search === b.search && a.dealer === b.dealer && a.createdAtFrom === b.createdAtFrom && a.createdAtTo === b.createdAtTo;

const QuotationsHistoryPage = () => {
	const { t } = useTranslation();
	const { showError } = useErrorToast();
	const { userRole } = useAuth();
	const showDealerFilter = userRole === RoleEnum.MASTER;
	const fetchAllQuotations = useFetchAllQuotations();

	const [draft, setDraft] = useState<QuotationFilters>(EMPTY_FILTERS);
	const [filters, setFilters] = useState<QuotationFilters>(EMPTY_FILTERS);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [selected, setSelected] = useState<Quotation | null>(null);
	const [showDetail, setShowDetail] = useState(false);
	const [exportProgress, setExportProgress] = useState<{ loaded: number; total: number } | null>(null);

	const applyFilters = (next: QuotationFilters) => {
		setFilters({ ...next, search: next.search.trim(), dealer: next.dealer.trim() });
		setPage(1);
	};

	const handleSearch = () => applyFilters(draft);

	const handleSearchReset = () => {
		setDraft(EMPTY_FILTERS);
		applyFilters(EMPTY_FILTERS);
	};

	const { data, isPending, isFetching, error } = useQuotations(filters, page, pageSize);

	const items = useMemo(() => data?.items ?? [], [data]);
	const lastKnownTotal = useRef(0);
	const dataFilters = useRef(filters);
	if (data) {
		lastKnownTotal.current = data.total;
		dataFilters.current = filters;
	}
	const totalCount = data?.total ?? lastKnownTotal.current;
	const isFilterChanging = !sameFilters(filters, dataFilters.current);
	const hasFilters = !sameFilters(draft, EMPTY_FILTERS) || !sameFilters(filters, EMPTY_FILTERS);

	const handleOpen = (row: Quotation) => {
		setSelected(row);
		setShowDetail(true);
	};

	const handleExport = async (format: ExportFormat) => {
		if (!totalCount) {
			showError(null, t('no_data_available_for_download', { defaultValue: 'Non ci sono dati disponibili per il download' }));
			return;
		}

		let rows: Quotation[];
		setExportProgress({ loaded: 0, total: totalCount });
		try {
			rows = await fetchAllQuotations(filters, (loaded, total) => setExportProgress({ loaded, total }));
		} catch (err) {
			showError(err, t('error_fetching_data', { defaultValue: 'Errore durante la ricerca del dato' }));
			return;
		} finally {
			setExportProgress(null);
		}

		const exportData = buildExportData(rows);
		const fileName = t('quotations_history', { defaultValue: 'Storico preventivi' }).replace(/ /g, '_');
		if (format === 'xlsx') downloadXLSX(exportData, fileName);
		else downloadCSV(exportData, fileName);
	};

	const exportLabel = exportProgress ? (
		<>
			<Spinner animation="border" size="sm" className="me-2" />
			{exportProgress.loaded.toLocaleString('it-IT')} / {exportProgress.total.toLocaleString('it-IT')}
		</>
	) : undefined;

	const columns = useMemo(
		() => createColumnsData(handleOpen) as Column<object>[],
		[] // eslint-disable-line react-hooks/exhaustive-deps
	);

	const searchLabel = t('quotations_history_search', {
		defaultValue: 'Cerca per codice, marca, modello, destinatario o utente',
	});
	const dateFromLabel = t('quotations_date_from', { defaultValue: 'Dal' });
	const dateToLabel = t('quotations_date_to', { defaultValue: 'Al' });
	const dealerLabel = t('dealer', { defaultValue: 'Dealer' });

	return (
		<Container fluid className="quotations-history-page">
			<Row>
				<Col md={6} className="display-6">
					{t('quotations_history', { defaultValue: 'Storico preventivi' })}
				</Col>
				<Col md={6} className="mt-4 mt-md-0 d-flex justify-content-md-end">
					<FileExportMenu
						isDisabled={isPending || exportProgress !== null}
						allowedFormats={['csv', 'xlsx']}
						handleExport={handleExport}
						label={exportLabel}
					/>
				</Col>
			</Row>

			<Card className="mt-4">
				<Card.Body className="py-3">
					<Form
						onSubmit={(event) => {
							event.preventDefault();
							handleSearch();
						}}
					>
						<Row className="g-2 align-items-end">
							<Col xs={12}>
								<Form.Control
									type="search"
									value={draft.search}
									placeholder={searchLabel}
									aria-label={searchLabel}
									onChange={(event) => {
										const value = event.target.value;
										setDraft((prev) => ({ ...prev, search: value }));
										if (value === '') applyFilters({ ...filters, search: '' });
									}}
								/>
							</Col>
							<Col md={3}>
								<Form.Label className="mb-1">{dateFromLabel}</Form.Label>
								<Form.Control
									type="date"
									value={draft.createdAtFrom}
									max={draft.createdAtTo || undefined}
									aria-label={dateFromLabel}
									onChange={(event) => setDraft((prev) => ({ ...prev, createdAtFrom: event.target.value }))}
								/>
							</Col>
							<Col md={3}>
								<Form.Label className="mb-1">{dateToLabel}</Form.Label>
								<Form.Control
									type="date"
									value={draft.createdAtTo}
									min={draft.createdAtFrom || undefined}
									aria-label={dateToLabel}
									onChange={(event) => setDraft((prev) => ({ ...prev, createdAtTo: event.target.value }))}
								/>
							</Col>
							{showDealerFilter && (
								<Col md={3}>
									<Form.Label className="mb-1">{dealerLabel}</Form.Label>
									<Form.Control
										type="search"
										value={draft.dealer}
										placeholder={dealerLabel}
										aria-label={dealerLabel}
										onChange={(event) => setDraft((prev) => ({ ...prev, dealer: event.target.value }))}
									/>
								</Col>
							)}
							<Col md={showDealerFilter ? 3 : 6} className="d-flex justify-content-end gap-2">
								{hasFilters && (
									<Button type="button" variant="light" className="text-nowrap" onClick={handleSearchReset}>
										{t('reset_filters', { defaultValue: 'Reset filtri' })}
									</Button>
								)}
								<Button type="submit" variant="secondary" className="text-nowrap">
									{t('search', { defaultValue: 'Cerca' })}
								</Button>
							</Col>
						</Row>
					</Form>
				</Card.Body>
			</Card>

			{error ? (
				<Card className="my-4">
					<Card.Body className="text-center text-danger py-5">
						{error instanceof Error && error.message ? error.message : t('error_loading_data')}
					</Card.Body>
				</Card>
			) : isFilterChanging ? (
				<TableSkeleton cols={columns.length} rows={pageSize} />
			) : (
				<ServerPaginatedTable
					key={`${JSON.stringify(filters)}-${pageSize}`}
					columns={columns}
					data={items}
					loading={isPending || isFetching}
					error={error}
					totalCount={totalCount}
					pageSize={pageSize}
					showPageSelector
					pageSizeOptions={PAGE_SIZE_OPTIONS}
					initialPage={page}
					onPageSizeChange={(newSize: number) => {
						setPageSize(newSize);
						setPage(1);
					}}
					loadFn={(skip: number, limit: number) => setPage(Math.floor(skip / limit) + 1)}
					dblClickCallback={(item: Quotation) => handleOpen(item)}
				/>
			)}

			<QuotationDetailOverlay
				quotation={selected}
				open={showDetail}
				onOpenChange={(open) => {
					setShowDetail(open);
					if (!open) setSelected(null);
				}}
			/>
		</Container>
	);
};

export default QuotationsHistoryPage;
